import { routePaths } from "@/App";
import { NavigationSelect } from "@/components/NavigationSelect";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { VehicleType } from "@/libs/api/types";
import { TourTaskItem } from "@/libs/tours-detail/components/TourTaskItem";
import { useTourNavigation } from "@/libs/tours-detail/hooks/useTourNavigation";
import { useTourStop } from "@/libs/tours-detail/hooks/useTourStop";

import { ChevronLeft, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router";

interface GatherTaskPickUpViewProps {
  tourId: string;
  stopId: string;
}

export default function GatherTaskPickUpView({
  tourId,
  stopId,
}: GatherTaskPickUpViewProps) {
  const { t } = useTranslation();

  const { currentStop } = useTourStop(stopId);

  const { navigateToNextStop } = useTourNavigation(currentStop);
  const navigate = useNavigate();

  if (!currentStop) {
    return <Navigate to={routePaths.createTourPath(tourId)} />;
  }

  return (
    <div className="h-dvh flex flex-col">
      <div className="bg-white px-4 py-6 pt-safe">
        <Link
          to={routePaths.createTourPath(tourId)}
          className="mb-8 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
          {t("common.back")}
        </Link>

        <div className="space-y-2">
          <header className="flex justify-between">
            <h3 className="text-xl font-semibold">
              {t("gatherPickup.header.title")}
            </h3>
          </header>

          <div className="flex items-center text-sm font-medium text-[#6A80A8]">
            <MapPin className="w-5 h-5 mr-2" strokeWidth={2.5} />
            {currentStop.address}
          </div>

          <NavigationSelect
            address={currentStop.address}
            vehicle={VehicleType.CAR}
          />
        </div>
      </div>

      <div className="p-4 h-full space-y-4 overflow-y-scroll flex-1 bg-slate-100">
        {currentStop.items.map((item) => (
          <TourTaskItem
            key={item.id}
            item={item}
            onClick={() => {
              navigate(
                routePaths.createGatherPickupDetailPath(
                  tourId,
                  stopId,
                  item.id,
                ),
              );
            }}
          />
        ))}
      </div>

      <div className="bg-white p-4 border-t-2 border-neutral pb-safe">
        <Button
          className="w-full"
          variant="secondary"
          onClick={navigateToNextStop}
        >
          {t("common.nextStop")}
        </Button>
      </div>
    </div>
  );
}

export function GatherTaskPickUpSkeleton() {
  return (
    <div className="h-dvh flex flex-col">
      <div className="bg-white px-4 py-6 pt-safe">
        <Skeleton className="mb-8 h-10 w-24 rounded" />

        <div className="space-y-2">
          <Skeleton className="h-7 w-48 rounded" />

          <div className="flex items-center">
            <Skeleton className="h-5 w-64 rounded" />
          </div>
        </div>
      </div>

      <div className="p-4 h-full space-y-4 overflow-y-scroll flex-1 bg-slate-100">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
            <Skeleton className="h-6 w-3/4 mb-2 rounded" />
            <Skeleton className="h-4 w-1/2 mb-3 rounded" />
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-1/4 rounded" />
              <Skeleton className="h-8 w-20 rounded" />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white p-4 border-t-2 border-neutral pb-safe">
        <Skeleton className="w-full h-12 rounded" />
      </div>
    </div>
  );
}
